import {
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Control, Controller, useForm } from 'react-hook-form';
import {
    useGetHostPayoutDetailsQuery,
    useSubmitHostPaymentDetailsMutation,
} from '../../../../src/app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../src/layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../src/shared/AuthRoles';
import FormTextField from '../../shared/FormTextField';
import toast from '../../shared/Toast';

const MASK_VISIBLE_TAIL = 4;

function maskValue(value: string): string {
    const v = value ?? '';
    if (v.length === 0) return '';
    const hidden = Math.max(0, v.length - MASK_VISIBLE_TAIL);
    return '*'.repeat(hidden) + v.slice(-MASK_VISIBLE_TAIL);
}

function digitsOnly(value: string): string {
    return value.replace(/\D/g, '');
}

interface MaskedRevealFieldProps {
    name: string;
    control: Control<any>;
    placeholder?: string;
    ariaLabel: string;
    maxLength?: number;
}

const MaskedRevealField = ({
    name,
    control,
    placeholder,
    ariaLabel,
    maxLength,
}: MaskedRevealFieldProps) => {
    const [revealed, setRevealed] = useState(false);

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { value, onChange, onBlur, ref },
                fieldState: { error },
            }) => {
                const raw = digitsOnly(value ?? '');
                const displayValue = revealed ? raw : maskValue(raw);

                const applyChange = (next: string) => {
                    let digits = digitsOnly(next);
                    if (maxLength != null) {
                        digits = digits.slice(0, maxLength);
                    }
                    onChange(digits);
                };

                const handleMaskedKeyDown = (
                    e: React.KeyboardEvent<HTMLInputElement>,
                ) => {
                    if (revealed) return;
                    if (e.ctrlKey || e.metaKey || e.altKey) return;

                    if (e.key === 'Backspace') {
                        e.preventDefault();
                        applyChange(raw.slice(0, -1));
                        return;
                    }

                    if (e.key === 'Delete') {
                        e.preventDefault();
                        applyChange('');
                        return;
                    }

                    if (e.key.length === 1 && /\d/.test(e.key)) {
                        e.preventDefault();
                        if (maxLength != null && raw.length >= maxLength) {
                            return;
                        }
                        applyChange(raw + e.key);
                    } else if (e.key.length === 1) {
                        e.preventDefault();
                    }
                };

                const handleMaskedPaste = (
                    e: React.ClipboardEvent<HTMLInputElement>,
                ) => {
                    if (revealed) return;
                    e.preventDefault();
                    applyChange(raw + e.clipboardData.getData('text'));
                };

                const handleBlur = () => {
                    setRevealed(false);
                    onBlur();
                };

                return (
                    <TextField
                        inputRef={ref}
                        size='small'
                        fullWidth
                        value={displayValue}
                        onChange={(e) => {
                            if (revealed) {
                                applyChange(e.target.value);
                            }
                        }}
                        onKeyDown={handleMaskedKeyDown}
                        onPaste={handleMaskedPaste}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        error={!!error}
                        helperText={error?.message}
                        autoComplete='off'
                        InputProps={{
                            inputProps: {
                                'aria-label': ariaLabel,
                                inputMode: 'numeric',
                                autoComplete: 'off',
                                'data-lpignore': 'true',
                                'data-1p-ignore': 'true',
                            },
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <Tooltip
                                        title={
                                            revealed
                                                ? 'Hide details'
                                                : 'Show details'
                                        }
                                    >
                                        <IconButton
                                            size='small'
                                            edge='end'
                                            type='button'
                                            onClick={() =>
                                                setRevealed((prev) => !prev)
                                            }
                                            aria-label={
                                                revealed
                                                    ? `Hide ${ariaLabel}`
                                                    : `Show ${ariaLabel}`
                                            }
                                        >
                                            {revealed ? (
                                                <VisibilityOffIcon fontSize='small' />
                                            ) : (
                                                <VisibilityIcon fontSize='small' />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                );
            }}
        />
    );
};

const PaymentDetails = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    useEffect(() => {
        if (loggedInUser.role !== 'owner_admin') {
            navigate(`/permissionDenied`);
        }
    }, []);

    const { data: payoutDetails, isLoading } = useGetHostPayoutDetailsQuery(
        `/owners/${loggedInUser?.company}/bank-account`,
        { skip: loggedInUser.role !== 'owner_admin' },
    );
    const [savePayoutDetails] = useSubmitHostPaymentDetailsMutation();

    const schema = z.object({
        accountName: z.coerce.string({ message: 'Required' }),
        bsb: z.coerce.string({ message: 'Required' }),
        accountNumber: z.coerce.string({ message: 'Required' }),
    });
    type PayoutDetails = z.infer<typeof schema>;
    const {
        reset,
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<PayoutDetails>({
        resolver: zodResolver(schema),
    });

    /** Remount masked fields so they never stay in "revealed" after save/refetch. */
    const [maskedBankFieldsKey, setMaskedBankFieldsKey] = useState(0);

    const theme = useTheme();

    useEffect(() => {
        if (payoutDetails) {
            const { accountName, bsb, accountNumber } = payoutDetails;
            reset({ accountName, bsb, accountNumber });
        }
    }, [payoutDetails]);
    const onSubmit = async (data: PayoutDetails) => {
        try {
            await savePayoutDetails([loggedInUser?.company, data]).unwrap();

            toast.success('Successfully saved account information');
            setMaskedBankFieldsKey((k) => k + 1);
        } catch (error: any) {
            toast.error(
                'Failed to add account information settings. Please try again.',
            );
            console.error('Payout submission error:', error);
        }
    };

    return isLoading ? (
        <LoadingComponent />
    ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '40px',
                }}
            >
                <Grid
                    sx={{
                        width: '100%',
                    }}
                >
                    <Typography variant='h1'>Payout Details</Typography>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            paddingTop: '10px',
                        }}
                    >
                        To receive payments for bookings made through the
                        platform, hosts must enter their bank account details.
                        This ensures we can securely and accurately transfer
                        earnings directly to you after each reservation.
                    </Typography>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography color='text.primary'>
                                Account Name
                            </Typography>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            <FormTextField
                                name='accountName'
                                control={control}
                                placeholder='ABC Pty Ltd'
                                type='string'
                            />
                        </Grid>
                    </Grid>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography color='text.primary'>BSB</Typography>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            <MaskedRevealField
                                key={`bsb-${maskedBankFieldsKey}`}
                                name='bsb'
                                control={control}
                                placeholder='123 456'
                                ariaLabel='BSB'
                                maxLength={6}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography color='text.primary'>
                                Account Number
                            </Typography>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            <MaskedRevealField
                                key={`acct-${maskedBankFieldsKey}`}
                                name='accountNumber'
                                control={control}
                                placeholder='1234 5678'
                                ariaLabel='Account number'
                                maxLength={10}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container columns={25} spacing={1}>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                paddingTop: '10px',
                            }}
                        >
                            By saving your bank details, you consent to us
                            storing this information securely in Xero and our
                            internal systems for the purpose of processing
                            payments, in accordance with our{' '}
                            <Link
                                to='/privacy'
                                style={{
                                    textDecoration: 'underline',
                                    fontSize: '12px',
                                    color: theme.palette.textAccent.main,
                                }}
                            >
                                Privacy Policy
                            </Link>
                            .
                        </Typography>

                        <Grid
                            item
                            xs={25}
                            lg={14}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <CustomButton
                                type='submit'
                                variant='contained'
                                color='primary'
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                Save
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default PaymentDetails;
